<template>
  <div class="home" :class="{ 'menu-open': isMenuOpen }">
    <Header :is-menu-open="isMenuOpen" :isLogoWhite="!isTabletView"></Header>
    <Menu
      :is-open="isMenuOpen"
      @toggleMenu="toggleMenu"
      :isWhiteMode="!isTabletView"
    ></Menu>
    <main class="hero">
      <MobileSlider :images="verticalPhotos" v-if="isTabletView" />
      <MainSlider
        v-else-if="!isTabletView"
        :verticalPhotos="verticalPhotos"
        :horizontalPhotos="horizontalPhotos"
        :isResourcesLoaded="isResourcesLoaded"
      />
      <div class="hero__actions">
        <Button
          class="hero__action button--withBorder"
          to="/about"
          :isWhiteMode="!isTabletView"
          >About us</Button
        >
        <Button
          class="hero__action button--withArrow"
          to="/portfolio"
          :with-arrow="true"
          :isWhiteMode="!isTabletView"
          >View portfolio</Button
        >
      </div>
    </main>
    <Footer :withWhiteLinks="!isTabletView"></Footer>
  </div>
</template>

<script>
import Header from "../components/Header";
import Footer from "../components/Footer";
import Menu from "../components/Menu";
import Button from "../components/Button";
import { MenuMixin, AwsMixin } from "../mixins";

import MobileSlider from "../components/Slider";
import MainSlider from "../components/MainSlider";

export default {
  mixins: [MenuMixin, AwsMixin],
  data: () => ({
    folderName: "slider",
    itemContext: "slider",
    isTabletView: false,
    horizontalPhotos: [],
    verticalPhotos: [],
    isResourcesLoaded: false,
    hasManuallyMoved: false,
    carouselIntervalId: null,
    autoCarouselStartIntervalId: null,
    movesCheckerTimeoutId: null,
    stopCarouselFlag: true,
    carouselChangePeriod: 4000,
  }),
  created() {
    this.checkTabletView();
    this.initDetectTabletListener();
  },
  methods: {
    initDetectTabletListener() {
      window.addEventListener("resize", () => this.checkTabletView());
    },
    checkTabletView() {
      this.isTabletView = window.matchMedia("(max-width: 768px)").matches;
    },
    loadVerticalImages() {
      this.getImagesFromS3Bucket(
        'vertical',
        this.itemContext,
        "verticalPhotos"
      );
    },
    loadHorizontalImages() {
      this.getImagesFromS3Bucket(
        'horizontal',
        this.itemContext,
        "horizontalPhotos"
      );
    },
    initSlider(item) {
      let items = item.querySelectorAll("li"),
        current = 0,
        xDown = null,
        yDown = null;

      const self = this;
      const nav = document.createElement("nav");
      const counter = document.createElement("div");

      nav.className = "slider__nav";
      counter.className = "counter";
      counter.innerHTML = "<span>1</span><span>" + items.length + "</span>";

      if (items.length > 1) {
        nav.appendChild(counter);
        item.appendChild(nav);
      }

      items[current].className = "slider__list-item current right";

      const carouselCallback = () => {
        // change slide only when it's allowed to run carousel
        // it is checked by flag "stopCarouselFlag"

        if (self.stopCarouselFlag) {
          self.stopCarouselFlag = false;
          return;
        } else {
          navigate("right");
        }
      };

      const carouselInitFunction = this.getCarouselInitFunction(
        carouselCallback
      );
      
      const navigate = (dir) => {
        Array.from(items).map((item) => (item.className = "slider__list-item"));

        if (dir === "right") {
          current = current < items.length - 1 ? current + 1 : 0;
        } else {
          current = current > 0 ? current - 1 : items.length - 1;
        }

        const nextCurrent = current < items.length - 1 ? current + 1 : 0;
        const prevCurrent = current > 0 ? current - 1 : items.length - 1;

        items[current].className = `slider__list-item current ${dir}`;
        items[prevCurrent].className = `slider__list-item prev_slide ${dir}`;
        items[nextCurrent].className = `slider__list-item next_slide`;

        if (dir === "left") {
          items[nextCurrent].className += ` ${dir}`;
          items[prevCurrent].classList.remove(dir);
        }

        counter.firstChild.textContent = current + 1;
      };

      const handleTouchStart = (evt) => {
        xDown = evt.touches[0].clientX;
        yDown = evt.touches[0].clientY;
      };

      const resetAutoCarouselStartChecker = () => {
        clearInterval(self.autoCarouselStartIntervalId);
        self.autoCarouselStartIntervalId = null;
      };

      const handleTouchMove = (evt) => {
        if (!xDown || !yDown) {
          return;
        }

        // logic below describes behavior:
        // when user swipes a slide, carousel auto change is stopped
        // after 5 seconds without moves, it restarts

        clearInterval(self.carouselIntervalId);

        self.carouselIntervalId = null;
        self.hasManuallyMoved = true;
        self.stopCarouselFlag = true;

        if (self.movesCheckerTimeoutId) {
          clearTimeout(self.movesCheckerTimeoutId);
          self.movesCheckerTimeoutId = null;
        }

        if (self.autoCarouselStartIntervalId) {
          resetAutoCarouselStartChecker();
        }

        self.movesCheckerTimeoutId = setTimeout(() => {
          self.hasManuallyMoved = false;
        }, self.carouselChangePeriod);

        self.autoCarouselStartIntervalId = setInterval(() => {
          if (!self.hasManuallyMoved) {
            navigate("right");
            carouselInitFunction();
            resetAutoCarouselStartChecker();
          }
        }, 1000);

        const xUp = evt.touches[0].clientX;
        const yUp = evt.touches[0].clientY;

        let xDiff = xDown - xUp;
        let yDiff = yDown - yUp;

        if (Math.abs(xDiff) > Math.abs(yDiff)) {
          if (xDiff > 0) {
            navigate("right");
          } else {
            navigate("left");
          }
        }

        xDown = null;
        yDown = null;
      };

      carouselInitFunction();

      // swipe navigation
      // from http://stackoverflow.com/a/23230280
      item.addEventListener("touchstart", handleTouchStart, { passive: true });
      item.addEventListener("touchmove", handleTouchMove, { passive: true });
    },
    getCarouselInitFunction(callback) {
      return () => {
        this.carouselIntervalId = setInterval(
          callback,
          this.carouselChangePeriod
        );
      };
    },
    initMobileSlider() {
      const self = this;
      const intervalPeriod = 50;

      const timerId = setInterval(() => {
        if (self.isVerticalPhotosInitialized) {
          const items = Array.from(document.querySelectorAll(".slider"));
          items.forEach(self.initSlider);
          clearInterval(timerId);
        }
      }, intervalPeriod);
    },
    runSliderSwitch() {
      this.loadVerticalImages();

      if (this.isTabletView) {
        this.initMobileSlider();
        this.isVerticalPhotosInitialized = false;
      } else {
        this.loadHorizontalImages();
      }
    },
  },
  mounted() {
    this.runSliderSwitch();
  },
  components: { Header, Footer, Menu, MobileSlider, Button, MainSlider },
};
</script>